import type { ChangelogVersion, Schema } from "@productlane/zero"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { JSONValue, Zero } from "@rocicorp/zero"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"

interface RestoreChangelogVersionArgs {
	zero: Zero<Schema>
	workspaceId: string
	changelogId: string
	version: ChangelogVersion
	defaultLanguage: string
}

// Shared by the version-history dialog and the preview banner: both write the
// translation row, plus the base row when the version is in the default language.
export async function restoreChangelogVersion({
	zero,
	workspaceId,
	changelogId,
	version,
	defaultLanguage,
}: RestoreChangelogVersionArgs): Promise<boolean> {
	// Resolve the row first; upserting a fresh id would leave two translation
	// rows for the same language behind.
	const existing = await zero.run(
		queries.changelogTranslation({ changelogId, language: version.language }),
	)
	const finalTranslationId = existing?.id ?? generateId()

	const fail = (error: unknown, action: string) => {
		console.error("Failed to restore version:", error)
		captureError(error, {
			domain: "general",
			action,
			workspaceId,
			extra: { changelogId, versionId: version.id },
		})
		toast.error({
			title: "Restoring this version failed",
			description: "Try again in a moment.",
		})
	}

	// One atomic write: translation row, base row (default language), and a new
	// version row (forced: fold-into-head would swallow the replaced state, and
	// the restore could not be undone). expectedVersion null: a restore is an
	// explicit request to replace whatever is current.
	const result = await zero.mutate(
		mutators.changelogs.saveContent({
			changelogId,
			translationId: finalTranslationId,
			language: version.language,
			title: version.title,
			notes: JSON.parse(JSON.stringify(version.notes)) as JSONValue,
			imageUrl: version.imageUrl,
			coverVideoUrl: version.coverVideoUrl ?? null,
			updateBaseRow: version.language === defaultLanguage,
			expectedVersion: null,
			snapshot: { id: generateId(), forceNewVersion: true },
		}),
	).client

	if (result.type === "error") {
		fail(result.error, "restore version")
		return false
	}

	toast.success({
		title: "Version restored",
		description: "The changelog has been restored to the selected version.",
	})

	analytics.track("restore-version", {
		changelogId,
		versionId: version.id,
		language: version.language,
	})

	return true
}
